/**
 * LookupField — a searchable single-select combobox.
 *
 * Replaces the "paste a UUID" inputs. The caller supplies an already-loaded
 * `options` array (see lib/useDirectory.js); filtering is client-side so
 * typing is instant and works against backends with no search endpoint.
 *
 * Option shape:
 *   { id, primary, secondary, meta, search }
 *     primary   — the name shown in bold
 *     secondary — supporting line (email, vehicle, stage…)
 *     meta      — right-aligned chip (role, score…)
 *     search    — lowercase haystack; falls back to primary+secondary+id
 *
 * Keyboard: ↑/↓ move, Enter select, Esc close/clear, Backspace on an empty
 * query clears the selection.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

const haystack = (o) =>
  (o.search ?? `${o.primary ?? ''} ${o.secondary ?? ''} ${o.meta ?? ''} ${o.id ?? ''}`).toLowerCase();

export default function LookupField({
  label,
  options = [],
  value,                 // selected id, or null
  onChange,              // (id | null, option | null) => void
  loading = false,
  error = null,
  required = false,
  placeholder = 'Search…',
  emptyHint = 'No matches',
  disabled = false,
  id: fieldId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  // Menu is positioned `fixed` from the anchor's rect so it escapes the
  // modal's `overflow: auto` scroll box — an absolutely-positioned menu gets
  // clipped there, which hides the options for the lower fields.
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const measure = () => {
    const el = wrapRef.current?.querySelector('.lookup-anchor');
    if (el) setRect(el.getBoundingClientRect());
  };

  // Re-measure while open; close on scroll/resize so the menu can't detach
  // from its field.
  useEffect(() => {
    if (!open) return;
    measure();
    const onScroll = (e) => {
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const selected = useMemo(
    () => options.find(o => o.id === value) || null,
    [options, value],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options.filter(o => haystack(o).includes(q)).slice(0, 50);
  }, [options, query]);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { setCursor(0); }, [query, open]);

  // Keep the highlighted row in view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const choose = (opt) => {
    onChange?.(opt ? opt.id : null, opt || null);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setCursor(c => Math.min(c + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      // Don't submit the surrounding form while picking.
      if (open && matches[cursor]) {
        e.preventDefault();
        choose(matches[cursor]);
      }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); setOpen(false); setQuery(''); }
    } else if (e.key === 'Backspace' && !query && selected) {
      choose(null);
    }
  };

  return (
    <div className="form-field lookup" ref={wrapRef} style={{ position: 'relative' }}>
      {label && (
        <label htmlFor={fieldId}>
          {label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}
        </label>
      )}

      {selected ? (
        /* Selected state — a chip with a clear button. */
        <div
          className="lookup-selected lookup-anchor"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 8px 7px 10px',
            background: 'var(--bg-glass)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-sm)',
            minHeight: 36,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selected.primary}
            </div>
            {selected.secondary && (
              <div className="mono text-xs text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selected.secondary}
              </div>
            )}
          </div>
          {!disabled && (
            <>
              <button
                type="button"
                className="btn small"
                onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
                title="Change"
              >
                Change
              </button>
              <button
                type="button"
                className="btn small"
                onClick={() => choose(null)}
                aria-label={`Clear ${label || 'selection'}`}
                title="Clear"
              >
                ✕
              </button>
            </>
          )}
        </div>
      ) : (
        <input
          id={fieldId}
          className="lookup-anchor"
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={query}
          placeholder={loading ? 'Loading…' : placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
      )}

      {/* Dropdown — fixed to the viewport so the modal's scroll box can't clip
          it. Flips above the field when there isn't room below. */}
      {open && !disabled && rect && (
        <div
          className="lookup-menu"
          role="listbox"
          ref={listRef}
          style={{
            position: 'fixed',
            left: rect.left,
            width: rect.width,
            ...(window.innerHeight - rect.bottom < 260 && rect.top > 260
              ? { bottom: window.innerHeight - rect.top + 4 }
              : { top: rect.bottom + 4 }),
            zIndex: 1200,           // above .modal-backdrop (1000)
            maxHeight: 240,
            overflowY: 'auto',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-lg)',
            padding: 4,
          }}
        >
          {/* When a value is already chosen, the input is replaced by the chip,
              so give the dropdown its own search box. */}
          {selected && (
            <input
              ref={inputRef}
              autoComplete="off"
              value={query}
              placeholder={placeholder}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              style={{
                width: '100%', marginBottom: 4, padding: '6px 8px',
                background: 'var(--bg-glass)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)', fontSize: 13, outline: 'none',
              }}
            />
          )}

          {loading && (
            <div className="mono text-xs text-muted" style={{ padding: '8px 10px' }}>Loading…</div>
          )}

          {!loading && matches.length === 0 && (
            <div className="mono text-xs text-muted" style={{ padding: '8px 10px', lineHeight: 1.6 }}>
              {query ? emptyHint : (options.length === 0 ? emptyHint : 'Start typing to search')}
            </div>
          )}

          {!loading && matches.map((o, i) => (
            <button
              key={o.id}
              type="button"
              role="option"
              aria-selected={i === cursor}
              data-idx={i}
              onMouseEnter={() => setCursor(i)}
              onClick={() => choose(o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', textAlign: 'left',
                padding: '7px 8px',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                background: i === cursor ? 'var(--bg-glass-hover)' : 'transparent',
                color: 'var(--text-primary)',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {o.primary}
                </span>
                {o.secondary && (
                  <span className="mono text-xs text-muted" style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {o.secondary}
                  </span>
                )}
              </span>
              {o.meta && <span className="pill">{o.meta}</span>}
            </button>
          ))}
        </div>
      )}

      {error && <div className="text-xs" style={{ color: 'var(--danger)', marginTop: 4 }}>{error}</div>}
    </div>
  );
}
